import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Users,
  Clock,
  Settings,
  BarChart3,
  Calendar,
  Zap,
  Search,
  Crosshair,
  TrendingUp,
  Target,
  Activity,
  Trash2,
  SlidersHorizontal,
  DollarSign,
  FileText,
} from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import Footer from "../components/Footer";

const CASE_ROW_1 = [
  {
    icon: Building2,
    title: "Industry",
    desc: "Manufacturing (Quality)",
  },
  {
    icon: Users,
    title: "Key Users",
    desc: "Quality Managers, Production Managers, Manufacturing Engineers, Plant Heads",
  },
  {
    icon: Clock,
    title: "Business Need",
    desc: "Identify defects and quality issues earlier and improve first-pass yield",
  },
];

const CASE_ROW_2 = [
  {
    icon: Settings,
    title: "Solution",
    desc: "Simpel Quality Intelligence",
  },
  {
    icon: BarChart3,
    title: "Impact",
    desc: "+2-6 percentage points in first-pass yield",
  },
  {
    icon: Calendar,
    title: "Timeframe",
    desc: "10-16 months payback",
  },
];

const WHO_BENEFITS_DATA = [
  {
    role: "Head of Quality / Quality Manager",
    improves:
      "Earlier visibility of defect patterns and quality issues. Faster, evidence-based focus for containment and improvement; stronger first-pass yield on the pilot scope.",
  },
  {
    role: "Production / Operations Manager",
    improves:
      "Fewer surprises downstream. Process adjustments guided by data; more stable output quality on the monitored line or family.",
  },
  {
    role: "Process / Manufacturing Engineering",
    improves:
      "Clear links between process conditions and quality outcomes. Better prioritisation of which parameters and steps to stabilise.",
  },
  {
    role: "Plant Head / Site Head",
    improves:
      "Higher yield and lower cost of poor quality on the pilot scope. Quality performance that supports customer and cost targets.",
  },
];

const RESULTS_DATA = [
  {
    icon: TrendingUp,
    subtitle: "First-pass yield on pilot product / line",
    title: "+2 to +6 percentage points",
  },
  {
    icon: Clock,
    subtitle: "Time to identify defect pattern / quality issue",
    title: "40% to 60% faster",
  },
  {
    icon: Trash2,
    subtitle: "Scrap and rework linked to late-detected defects (pilot scope)",
    title: "20% to 40% reduction",
  },
  {
    icon: SlidersHorizontal,
    subtitle: "Inspection and investigation effort focused on high-risk areas",
    title: "Clear improvement in prioritization",
  },
  {
    icon: Calendar,
    subtitle: "Typical payback window",
    title: "10 - 16 months",
  },
  {
    icon: DollarSign,
    subtitle: "First-year value (yield + quality cost)",
    title: "$180k - $480k (scale-dependent)",
  },
];

const PROCESS_STEPS_QUALITY = [
  {
    id: "integrate",
    title: "Integrate data",
    desc: "Bring production and inspection data together.",
    icon: FileText,
    bgGradient: "bg-gradient-to-br from-[#93C5FD] to-[#3B82F6]",
    glow: "shadow-[0_0_24px_rgba(59,130,246,0.35)]",
  },
  {
    id: "analyze",
    title: "Analyze patterns",
    desc: "Identify defect trends and process correlations.",
    icon: Search,
    bgGradient: "bg-gradient-to-br from-[#E9D5FF] to-[#A855F7]",
    glow: "shadow-[0_0_24px_rgba(168,85,247,0.35)]",
  },
  {
    id: "prioritize",
    title: "Prioritize action",
    desc: "Focus on high-risk products, steps or shifts.",
    icon: Zap,
    bgGradient: "bg-gradient-to-br from-[#FEF08A] to-[#EAB308]",
    glow: "shadow-[0_0_24px_rgba(234,179,8,0.35)]",
  },
  {
    id: "correct",
    title: "Take corrective action",
    desc: "Adjust process, tighten control or focus inspection.",
    icon: Settings,
    bgGradient: "bg-gradient-to-br from-[#BBF7D0] to-[#22C55E]",
    glow: "shadow-[0_0_24px_rgba(34,197,94,0.35)]",
  },
  {
    id: "improve",
    title: "Improve yield",
    desc: "Lift first-pass yield and reduce quality cost.",
    icon: BarChart3,
    bgGradient: "bg-gradient-to-br from-[#FBCFE8] to-[#EC4899]",
    glow: "shadow-[0_0_24px_rgba(236,72,153,0.35)]",
  },
];

const QualityIntelligence = () => {
  const navigate = useNavigate();

  // Scroll to top on page mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate("/ai-opportunities?tab=Industries");
    }
  };

  const containerRef = useRef(null);

  // Scroll tracking across sticky hero track
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const overlayY = useTransform(
    scrollYProgress,
    [0, 0.22, 0.75, 0.98],
    ["100%", "0%", "0%", "-100%"]
  );

  const overlayOpacity = useTransform(
    scrollYProgress,
    [0, 0.06, 0.22, 0.75, 0.95, 1],
    [0, 0.6, 1, 1, 0.7, 0]
  );

  const handleScrollDown = () => {
    const el = document.getElementById("case-at-a-glance");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else if (containerRef.current) {
      const top = containerRef.current.offsetTop;
      const height = containerRef.current.offsetHeight;
      window.scrollTo({
        top: top + height,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 flex flex-col font-sans selection:bg-[#00D2EE] selection:text-black">
      {/* Top Floating App Bar */}
      <header className="sticky top-0 z-50 w-full border-b border-white/10 bg-[#000000]/80 backdrop-blur-md">
        <div className="mx-auto flex h-[76px] sm:h-[82px] max-w-[1440px] items-center justify-between px-6 lg:px-[70px]">
          {/* Left Brand Identity */}
          <Link
            to="/"
            className="flex items-center gap-3 sm:gap-3.5 group select-none shrink-0"
          >
            <img
              src="/images/simpel-icon-mark.png"
              alt="Simpel"
              className="h-[34px] sm:h-[38px] w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <div className="flex flex-col justify-center">
              <span className="text-[22px] sm:text-[25px] font-bold leading-none tracking-tight text-white">
                Simpel
              </span>
              <span className="mt-[3px] text-[7.5px] sm:text-[8px] font-bold uppercase tracking-[2.2px] sm:tracking-[2.5px] text-white/70">
                AUTOMATION SIMPLIFIED
              </span>
            </div>
          </Link>

          {/* Right Breadcrumb & Back Button */}
          <div className="flex items-center gap-5 sm:gap-8">
            <div className="hidden sm:block border-b-2 border-[#2F6DF6] pb-0.5">
              <span className="text-[14px] sm:text-[15px] font-medium text-[#2F6DF6] tracking-wide select-none">
                Industries / Manufacturing
              </span>
            </div>

            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-full bg-[#00D2EE] px-5 py-2 sm:px-6 sm:py-2.5 text-[13px] sm:text-[14px] font-bold text-black transition-all duration-200 hover:brightness-105 hover:shadow-[0_4px_16px_rgba(0,210,238,0.35)] active:scale-95 cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 stroke-[2.5]" />
              <span>Back to Cognitive AI</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="flex-1 w-full bg-[#000000]">
        {/* =========================================================================
            Hero Section with Scroll Pinning Animation (matching Screenshot 1)
            Background: /images/insights/quality-intelligence-hero.jpg
           ========================================================================= */}
        <section ref={containerRef} className="relative w-full h-[270vh]">
          {/* Sticky Viewport Stage */}
          <div className="sticky top-[76px] sm:top-[82px] h-[calc(100vh-76px)] sm:h-[calc(100vh-82px)] w-full overflow-hidden bg-[#000000]">
            {/* Underlying Hero Section Photo - 100% crystal clear at scroll 0 */}
            <div className="absolute inset-0 w-full h-full">
              <img
                src="/images/insights/quality-intelligence-hero.jpg"
                alt="Quality Intelligence - Factory Floor Production & Inspection Terminal"
                className="w-full h-full object-cover object-center block select-none"
              />
            </div>

            {/* Frosted Blur Overlay Section with Pinning Animation */}
            <motion.div
              style={{
                y: overlayY,
                opacity: overlayOpacity,
              }}
              className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 sm:px-12 lg:px-20 text-center"
            >
              {/* Frosted Glass Background Tint */}
              <div
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{
                  backgroundColor: "rgba(15, 27, 41, 0.94)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              />

              {/* Foreground Content */}
              <div className="relative z-10 max-w-[1240px] flex flex-col items-center pt-4 sm:pt-0">
                {/* Category Tag matching Screenshot 1 */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[3px] text-white/70 mb-5 sm:mb-6">
                  MANUFACTURING - QUALITY INTELLIGENCE
                </p>

                {/* Main Heading matching Screenshot 1 */}
                <h1 className="text-[28px] sm:text-[38px] md:text-[46px] lg:text-[50px] xl:text-[54px] font-bold leading-[1.2] sm:leading-[1.18] text-white tracking-tight">
                  <span className="block whitespace-nowrap">
                    Quality Intelligence: Catching defect patterns
                  </span>
                  <span className="block mt-1 sm:mt-2">
                    earlier with{" "}
                    <span className="bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                      production and inspection data
                    </span>
                  </span>
                </h1>

                {/* Subtitle matching Screenshot 1 */}
                <div className="mt-5 sm:mt-6 max-w-[840px] text-[13.5px] sm:text-[15px] md:text-[15.5px] leading-[24px] sm:leading-[26px] text-white/80 text-center">
                  <p>
                    How Simpel Quality Intelligence analyzes production data and inspection information to identify defects,
                  </p>
                  <p className="mt-0.5">
                    patterns and quality issues earlier — so teams act upstream and protect first-pass yield.
                  </p>
                </div>

                {/* 4 Feature Items in a horizontal row matching Screenshot 1 */}
                <div className="mt-8 sm:mt-10 flex flex-wrap items-center justify-center gap-6 sm:gap-10 max-w-[980px]">
                  {/* Item 1 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Target className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Early defect detection
                    </span>
                  </div>

                  <div className="hidden sm:block h-6 w-[1px] bg-white/20" />

                  {/* Item 2 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <BarChart3 className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Process insights by step
                    </span>
                  </div>

                  <div className="hidden sm:block h-6 w-[1px] bg-white/20" />

                  {/* Item 3 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <Search className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Focused inspection effort
                    </span>
                  </div>

                  <div className="hidden sm:block h-6 w-[1px] bg-white/20" />

                  {/* Item 4 */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00D2EE]/20 text-[#00D2EE] border border-[#00D2EE]/30">
                      <TrendingUp className="h-4 w-4 stroke-[2.2]" />
                    </div>
                    <span className="text-[12px] sm:text-[13px] font-medium text-slate-200 text-left leading-tight">
                      Higher fir-pass yield
                    </span>
                  </div>
                </div>

                {/* Down Arrow with Dashed Line matching Screenshot 1 */}
                <div
                  onClick={handleScrollDown}
                  className="mt-10 sm:mt-12 flex flex-col items-center cursor-pointer group select-none"
                >
                  <svg
                    width="10"
                    height="100"
                    viewBox="0 0 10 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="opacity-75 transition-opacity group-hover:opacity-100"
                  >
                    <line
                      x1="5"
                      y1="0"
                      x2="5"
                      y2="90"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                    <path
                      d="M5 90L10 82M5 90L0 82"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* =========================================================================
            2. Case at a glance Section (matching Screenshot 2)
            Background: rgba(245, 250, 254, 1)
           ========================================================================= */}
        <section
          id="case-at-a-glance"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Case at a glance
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Single Box with 2 Rows x 3 Cols (matching Screenshot 2) */}
            <div className="rounded-[16px] bg-white border border-[#E2EDF8] overflow-hidden shadow-sm divide-y divide-[#E2EDF8]">
              {/* Row 1: Industry, Key Users, Business Need */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2EDF8]">
                {CASE_ROW_1.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start gap-4 p-6 sm:p-7">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                        <Icon className="h-5 w-5 stroke-[2]" />
                      </div>
                      <div>
                        <h3 className="text-[14px] sm:text-[15px] font-bold text-[#0F172A]">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Row 2: Solution, Impact, Timeframe */}
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2EDF8]">
                {CASE_ROW_2.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start gap-4 p-6 sm:p-7">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                        <Icon className="h-5 w-5 stroke-[2]" />
                      </div>
                      <div>
                        <h3 className="text-[14px] sm:text-[15px] font-bold text-[#0F172A]">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                          {item.desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            3. The Context Section (matching Screenshot 4)
            Background: #041421
            Right: Conveyor Inspection Camera & Monitor showing PASS & Detection Insights
            Left: The Context heading & narrative text
           ========================================================================= */}
        <section
          id="the-context"
          className="relative w-full overflow-hidden bg-[#041421] min-h-[310px] sm:h-[320px] lg:h-[330px] flex items-center text-white"
        >
          {/* Base Background Image: Flush to the VERY RIGHT of the section with zero margin/gap */}
          <div className="absolute right-0 top-0 bottom-0 h-full w-full sm:w-[58%] md:w-[54%] lg:w-[52%] xl:w-[50%] pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/quality-intelligence-context.jpg"
              alt="Quality Intelligence Context - Inspection camera and terminal showing PASS"
              className="h-full w-full object-cover object-right"
            />
            {/* Left progressive fade on the image to seamlessly blend into #041421 */}
            <div
              className="absolute inset-y-0 left-0 w-32 sm:w-44 lg:w-56 pointer-events-none"
              style={{
                background: "linear-gradient(90deg, #041421 0%, rgba(4, 20, 33, 0.85) 35%, transparent 100%)",
              }}
            />
          </div>

          {/* Left Solid Dark Base to guarantee zero background bleed */}
          <div
            className="absolute inset-y-0 left-0 w-[46%] pointer-events-none bg-[#041421]"
          />

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-10 sm:py-12 lg:py-0">
            <div className="max-w-[480px] sm:max-w-[520px] lg:max-w-[550px]">
              {/* Header row with horizontal divider line */}
              <div className="flex items-center gap-6 mb-5 sm:mb-6">
                <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                  The Context
                </h2>
                <div className="flex-1 h-[1px] bg-[#CEDDF1]/30" />
              </div>

              {/* Paragraph 1 */}
              <p className="text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/85">
                Manufacturing quality is won in the process, not only at final inspection. Production systems and inspection stations generate rich data — measurements, pass/fail outcomes, process parameters and timestamps.
              </p>

              {/* Paragraph 2 */}
              <p className="mt-4 sm:mt-5 text-[13px] sm:text-[14px] lg:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/85">
                When that information is analysed together, patterns emerge: which steps drift, which products or shifts show higher variation, which defects cluster by cause. Plants that see those patterns early can adjust the process, focus inspection where it matters and lift first-pass yield.
              </p>
            </div>
          </div>
        </section>

        {/* =========================================================================
            4. What the solution does Section (matching Screenshot 1)
            Background: rgba(245, 250, 254, 1)
           ========================================================================= */}
        <section
          id="what-the-solution-does"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-12 justify-between">
              {/* Left Column: Solution Card matching Screenshot 1 (676px x 308px, Radius 12px, Shadow 4px 4px 24px rgba(0,0,0,0.25)) */}
              <div className="w-full lg:w-[676px] shrink-0 flex justify-center lg:justify-start">
                <div
                  style={{
                    boxShadow: "4px 4px 24px 0px rgba(0, 0, 0, 0.25)",
                    borderRadius: "12px",
                  }}
                  className="w-full max-w-[676px] h-[240px] sm:h-[280px] lg:h-[308px] overflow-hidden rounded-[12px] bg-white border border-[#E2EDF8]"
                >
                  <img
                    src="/images/insights/quality-intelligence-solution.jpg"
                    alt="Quality Intelligence Terminal & Insights Interface"
                    className="w-full h-full object-cover object-center block select-none"
                  />
                </div>
              </div>

              {/* Right Column: Narrative Details */}
              <div className="flex-1 max-w-[560px] flex flex-col justify-center">
                {/* Header row with horizontal line matching Screenshot 1 */}
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    What the solution does
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Production and inspection data feed a quality intelligence layer that surfaces defect trends, process correlations and early warning signals. Teams see where issues concentrate — by product, step, shift or machine — and can prioritise containment and root-cause work with evidence.
                </p>

                <p className="mt-5 sm:mt-6 text-[13.5px] sm:text-[14.5px] lg:text-[15px] leading-[24px] sm:leading-[26px] text-[#64748B]">
                  Findings sit alongside existing quality systems so decisions stay in the same operational flow. Access and data handling follow plant security and compliance rules.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            5. The challenge Section (matching Screenshot 3)
            Background: #FFFFFF (White)
            Left: The challenge heading + narrative paragraph
            Right: 2 Numbered Problem Cards (01, 02)
           ========================================================================= */}
        <section
          id="the-challenge"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Left Column: Heading + Narrative */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-6 mb-6 sm:mb-7">
                  <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                    The challenge
                  </h2>
                  <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
                </div>

                <p className="text-[13.5px] sm:text-[14px] leading-[23px] sm:leading-[25px] text-[#64748B]">
                  Quality issues are often identified late, which can lead to higher scrap, rework and cost. The challenge is to connect production and inspection data, spot defect patterns earlier and take action closer to the source.
                </p>
              </div>

              {/* Right Column: 2 Numbered Problem Cards */}
              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {/* Card 01 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      01
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Siloed Data
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Production and inspection insights are
                      <br className="hidden sm:inline" /> often in separate systems, making
                      <br className="hidden sm:inline" /> patterns harder to spot.
                    </p>
                  </div>
                </div>

                {/* Card 02 */}
                <div className="rounded-[16px] bg-[#F5FAFE] border border-[#E2EDF8] p-6 sm:p-7 flex flex-col justify-between transition-shadow duration-200 hover:shadow-[0_4px_16px_rgba(15,23,42,0.04)]">
                  <div>
                    <span className="text-[26px] sm:text-[28px] font-bold text-[#00D2EE] tracking-tight leading-none">
                      02
                    </span>
                    <h3 className="text-[16px] sm:text-[17px] font-bold text-[#0F172A] mt-3.5 mb-2 leading-snug">
                      Late detection
                    </h3>
                    <p className="text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      Issues are sometimes identified only
                      <br className="hidden sm:inline" /> at final inspection, leading to higher
                      <br className="hidden sm:inline" /> rework and scrap.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            6. Who benefits Section (matching Screenshot 4)
            Background: /images/insights/quality-intelligence-hero.jpg
            Table: Fill 1,280px, Radius 12px, rgba(9, 21, 40, 0.6), blur 20px
            Header: Height 49px, rgba(14, 29, 53, 1), borderBottom rgba(26, 46, 76, 1)
           ========================================================================= */}
        <section
          id="who-benefits"
          className="relative w-full overflow-hidden bg-[#020B18] py-16 sm:py-20 lg:py-24 text-white"
        >
          {/* Base Background Image: Hero Photo */}
          <div className="absolute inset-0 w-full h-full select-none pointer-events-none">
            <img
              src="/images/insights/quality-intelligence-hero.jpg"
              alt="Who Benefits Background"
              className="h-full w-full object-cover object-center"
            />
            {/* Dark glass overlay tint */}
            <div className="absolute inset-0 bg-[#020B18]/60 backdrop-blur-[2px]" />
          </div>

          <div className="relative z-10 mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Who benefits
              </h2>
              <div className="flex-1 h-[1px] bg-white/20" />
            </div>

            {/* Subtitle matching Screenshot 4 */}
            <p className="text-[13.5px] sm:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/80 mb-8 sm:mb-10 max-w-[1040px]">
              Five roles gain when every interaction is better informed — continuously, not only on a pilot day.
            </p>

            {/* Glassmorphic Table Container */}
            <div
              style={{
                backgroundColor: "rgba(9, 21, 40, 0.28)",
                backdropFilter: "blur(16px)",
                WebkitBackdropFilter: "blur(16px)",
                border: "1px solid rgba(26, 46, 76, 1)",
              }}
              className="w-full max-w-[1280px] mx-auto rounded-[12px] overflow-hidden"
            >
              {/* Table Header: Height 49px, rgba(14, 29, 53, 1), borderBottom rgba(26, 46, 76, 1) */}
              <div
                style={{
                  backgroundColor: "rgba(14, 29, 53, 0.65)",
                  borderBottomColor: "rgba(26, 46, 76, 1)",
                }}
                className="grid grid-cols-12 px-6 h-[49px] items-center border-b text-[11px] sm:text-[12px] font-bold tracking-[1.5px] uppercase text-white/90"
              >
                <div className="col-span-12 sm:col-span-4 lg:col-span-4">Role</div>
                <div className="hidden sm:block sm:col-span-8 lg:col-span-8">What improves for them</div>
              </div>

              {/* Table Rows with divider matching rgba(26, 46, 76, 1) */}
              <div className="divide-y" style={{ borderColor: "rgba(26, 46, 76, 1)" }}>
                {WHO_BENEFITS_DATA.map((item) => (
                  <div
                    key={item.role}
                    style={{ borderColor: "rgba(26, 46, 76, 1)" }}
                    className="grid grid-cols-12 px-6 py-4 sm:py-5 items-start gap-2 sm:gap-6 hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="col-span-12 sm:col-span-4 lg:col-span-4 text-[14px] sm:text-[15px] font-bold text-white leading-snug">
                      {item.role}
                    </div>
                    <div className="col-span-12 sm:col-span-8 lg:col-span-8 text-[13px] sm:text-[13.5px] leading-[22px] text-white/80">
                      <span className="sm:hidden text-[11px] uppercase tracking-wider text-slate-400 font-semibold block mb-1">
                        What improves:
                      </span>
                      {item.improves}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            7. Results organizations typically see Section (matching Screenshot 5)
            Background: rgba(245, 250, 254, 1)
            6 Metric Cards (3 cols x 2 rows)
           ========================================================================= */}
        <section
          id="results-organizations-typically-see"
          className="w-full py-16 sm:py-20 lg:py-24 text-slate-900"
          style={{ backgroundColor: "rgba(245, 250, 254, 1)" }}
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-8 sm:mb-10">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                Results organizations typically see
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* 6 Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {RESULTS_DATA.map((card, idx) => {
                const Icon = card.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-start gap-4 sm:gap-5 rounded-[14px] sm:rounded-[16px] bg-white border border-[#EAF2FA] p-6 sm:p-7 shadow-[0_2px_12px_rgba(15,23,42,0.02)] transition-all duration-200 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] hover:-translate-y-0.5"
                  >
                    {/* Circular Blue Icon Badge */}
                    <div className="flex h-13 w-13 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-full bg-[#EBF5FF] text-[#007AFF]">
                      <Icon className="h-6 w-6 stroke-[2]" />
                    </div>

                    {/* Text Details */}
                    <div className="flex flex-col gap-1.5 pt-0.5">
                      <span className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {card.subtitle}
                      </span>
                      <h3 className="text-[15.5px] sm:text-[16.5px] font-bold text-[#0F172A] leading-snug">
                        {card.title}
                      </h3>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* =========================================================================
            8. How it works in practice Section (matching Screenshot 1)
            Background: #FFFFFF (White)
            5-Step Process Flow: Integrate data -> Analyze patterns -> Prioritize action -> Take corrective action -> Improve yield
            Bottom: Head of Quality Quote Box
           ========================================================================= */}
        <section
          id="how-it-works-in-practice"
          className="w-full py-16 sm:py-20 lg:py-24 bg-white text-slate-900"
        >
          <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header row with horizontal divider line */}
            <div className="flex items-center gap-6 mb-3 sm:mb-4">
              <h2 className="text-[28px] sm:text-[34px] lg:text-[40px] font-bold tracking-tight bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent shrink-0">
                How it works in practice
              </h2>
              <div className="flex-1 h-[1px] bg-[#E2E8F0]" />
            </div>

            {/* Subtitle matching Screenshot 1 */}
            <p className="text-[13.5px] sm:text-[14px] lg:text-[14.5px] leading-[23px] text-[#64748B] mb-12 sm:mb-16 max-w-[1040px]">
              From production data to corrective action — helping teams detect issues earlier, take targeted action and improve yield.
            </p>

            {/* 5-Step Process Flow with Connectors */}
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-2">
              {PROCESS_STEPS_QUALITY.map((step, idx) => {
                const Icon = step.icon;
                return (
                  <React.Fragment key={step.id}>
                    {/* Step Item */}
                    <div className="flex flex-col items-center text-center max-w-[210px] w-full group">
                      {/* Colored Circle Badge matching design system */}
                      <div
                        className={`flex h-16 w-16 sm:h-18 sm:w-18 items-center justify-center rounded-full ${step.bgGradient} ${step.glow} text-white transition-transform duration-300 group-hover:scale-110 mb-4 sm:mb-5`}
                      >
                        <Icon className="h-7 w-7 sm:h-8 sm:w-8 stroke-[2.2]" />
                      </div>

                      {/* Step Title */}
                      <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] mb-2">
                        {step.title}
                      </h3>

                      {/* Step Description */}
                      <p className="text-[12.5px] sm:text-[13px] leading-[19px] text-[#64748B]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Arrow between steps (hidden on last step) */}
                    {idx < PROCESS_STEPS_QUALITY.length - 1 && (
                      <div className="hidden lg:flex items-center justify-center shrink-0 px-2 text-[#00D2EE]">
                        <ArrowRight className="h-6 w-6 stroke-[2.5]" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Quote Callout Box matching Screenshot 1 */}
            <div className="mt-12 sm:mt-16 w-full rounded-[14px] sm:rounded-[18px] bg-[#F5FAFE] border border-[#E2EDF8] py-6 sm:py-7 px-6 sm:px-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 shadow-[0_2px_12px_rgba(15,23,42,0.02)]">
              <div className="flex items-start gap-3.5">
                <span className="text-[#00D2EE] text-[32px] sm:text-[40px] font-serif leading-none select-none -mt-1">
                  &ldquo;
                </span>
                <p className="text-[14.5px] sm:text-[15.5px] font-medium text-[#1E293B] leading-[25px] sm:leading-[27px]">
                  &ldquo;We always had inspection data. What changed was seeing patterns across production and inspection together — early enough to act on the process, not only on the finished piece. That is where first-pass yield moved.&rdquo;
                </p>
              </div>

              <div className="shrink-0 lg:border-l-2 lg:border-[#00D2EE] lg:pl-6 pt-4 lg:pt-0 border-t lg:border-t-0 border-[#E2EDF8]">
                <span className="block text-[14px] sm:text-[14.5px] font-bold text-[#0F172A] leading-tight">
                  Head of Quality
                </span>
                <span className="block text-[12px] sm:text-[12.5px] text-[#64748B] mt-1">
                  Manufacturing Plant
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================================
            9. CTA Banner Section (matching Screenshot 4)
            Background: /images/insights/quality-intelligence-cta.jpg
            Left: Solid Dark (#050B14) + Progressive Blur & Gradient Overlay
            Right: Exposed Manufacturing Rollers/Bearings Assembly
            CTA: Request a Consultation ->
           ========================================================================= */}
        <section
          id="cta-banner"
          className="relative w-full overflow-hidden bg-[#050B14] flex items-center min-h-[270px] lg:h-[280px] text-white"
        >
          {/* Base Background Image: Full span with right-aligned focus */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none overflow-hidden">
            <img
              src="/images/insights/quality-intelligence-cta.jpg"
              alt="Turn Quality data into Higher yield and lower cost"
              className="h-full w-full object-cover object-right"
            />
          </div>

          {/* Linear Gradient Overlay: 100% solid #050B14 on the left, smoothly fading out to the right */}
          <div
            className="absolute inset-0 w-full h-full pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, #050B14 0%, #050B14 38%, rgba(5, 11, 20, 0.95) 48%, rgba(5, 11, 20, 0.4) 62%, rgba(5, 11, 20, 0) 78%)",
            }}
          />

          {/* Figma Progressive Blur Layer */}
          <div
            className="absolute inset-y-0 left-0 w-[60%] pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, rgba(5, 11, 20, 0.85) 0%, rgba(5, 11, 20, 0.5) 45.73%, rgba(5, 11, 20, 0) 64.69%)",
              backdropFilter: "blur(14px)",
              WebkitBackdropFilter: "blur(14px)",
              maskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
              WebkitMaskImage: "linear-gradient(to right, black 45.73%, transparent 64.69%)",
            }}
          />

          {/* Content Container (Max width 1440px matching layout) */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-8 sm:py-10 lg:py-0 flex flex-col justify-center">
            <div className="max-w-[660px] w-full">
              {/* Heading matching Screenshot 4 (exact 2 lines) */}
              <h2 className="text-[24px] sm:text-[28px] lg:text-[32px] xl:text-[34px] font-bold tracking-tight leading-[1.2]">
                <span className="block whitespace-normal sm:whitespace-nowrap bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                  Turn Quality data into Higher yield and
                </span>
                <span className="block mt-0.5 bg-gradient-to-r from-[#00D2EE] to-[#2B7FFF] bg-clip-text text-transparent">
                  lower cost.
                </span>
              </h2>

              {/* Subtitle matching Screenshot 4 */}
              <p className="mt-3.5 sm:mt-4 text-[13.5px] sm:text-[14.5px] leading-[22px] sm:leading-[24px] text-white/85 max-w-[540px]">
                See how Simpel Quality Intelligence can help your plant detect issues earlier, improve first-pass yield and reduce rework.
              </p>

              {/* CTA Button matching Screenshot 4 */}
              <div className="mt-6 sm:mt-7">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3 text-[14px] sm:text-[15px] font-semibold text-[#0F172A] shadow-md transition-all duration-200 hover:bg-slate-100 hover:shadow-lg hover:-translate-y-0.5"
                >
                  Request a Consultation
                  <ArrowRight className="h-4 w-4 stroke-[2.5] text-[#0F172A]" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Anchor for future sections */}
        <div id="content-start" />
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default QualityIntelligence;

